import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { cn } from "@/lib/utils";

// The app's controls are label-and-input pairs with a value callback, not the
// bare DOM primitives shadcn ships. These wrappers keep the call sites the
// shape they have always had while the primitives underneath become shadcn's.
//
// The 12px on the wrapper is load-bearing: Preflight gives form controls
// `font: inherit`, so the input inside picks it up.
const fieldClass = "grid gap-1.5 text-xs text-muted-foreground";

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  className,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  className?: string;
}) {
  return (
    <label className={cn(fieldClass, className)}>
      {label && <span>{label}</span>}
      <Input type={type} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

export function TextareaField({
  label,
  value,
  onChange,
  rows = 4,
  mono,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  mono?: boolean;
}) {
  return (
    <label className={fieldClass}>
      <span>{label}</span>
      <Textarea
        className={cn(mono && "font-mono")}
        value={value}
        rows={rows}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export type SelectOption = string | { value: string; label: string };

export function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
}) {
  return (
    <label className={fieldClass}>
      <span>{label}</span>
      {/* Native select on purpose: these are two- and three-option lists, and
          a portalled popup would be more machinery than the case warrants. */}
      <select
        className="w-full rounded-lg border border-border bg-surface-input px-3 py-2.5 text-foreground transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/15"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) =>
          typeof option === "string" ? (
            <option key={option}>{option}</option>
          ) : (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ),
        )}
      </select>
    </label>
  );
}

export function ToggleField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2.5 pt-5 text-foreground">
      <Checkbox checked={checked} onCheckedChange={onChange} />
      <span>{label}</span>
    </label>
  );
}
